import React, { useState } from 'react';
import axios from 'axios';
import { FormattedMessage, useIntl } from 'react-intl';
import { Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from '@material-ui/core';
import Swal from 'sweetalert2';
import Config from '../../commons/config/Config.js';
import * as Utils from '../../commons/utils/Utils.js';

/**
 * VEC-3432 Scenarios 07 y 08 — importación masiva del catálogo de mano de obra.
 *
 * Flujo: descargar plantilla -> completar -> subir -> resumen con importados y detalle de errores
 * por fila -> descargar solo las filas erróneas para corregirlas y reimportar esas.
 *
 * El upload va con axios + FormData en lugar de `react-dropzone-component` (que es lo que usan los
 * importadores legacy de Personas/Combustibles): ese patrón arrastra jQuery y componentes de clase,
 * y este módulo es funcional con hooks. No suma dependencias nuevas: axios ya está.
 */
const BASE_URL = '/inventario/mano-obra';

const InventarioManoObraImportModal = (props) => {
  const intl = useIntl();

  const [archivo, setArchivo] = useState(null);
  const [importando, setImportando] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const [resultado, setResultado] = useState(null);

  const descargarBlob = (filename, data) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    // dataBlob devuelve el filename tal como viene del header, con las comillas incluidas.
    link.setAttribute('download', String(filename).replace(/"/g, ''));
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDescargarPlantilla = () => {
    setDescargando(true);
    Utils.dataBlob(`${BASE_URL}/plantilla-importacion`, 'GET', {})
      .then((res) => descargarBlob(res.filename, res.data))
      // Utils.dataBlob ya avisa el error por Swal; el catch solo evita el unhandled rejection
      // para que corra el finally que apaga el spinner.
      .catch(() => {})
      .finally(() => setDescargando(false));
  };

  /**
   * Los motivos que arma el import ya son texto; los que vienen de una excepción del Service son
   * claves i18n con su context en `motivosContext` (alineado por índice). translateBackendKey
   * traduce las claves e ignora el texto, así que sirve para ambos casos.
   */
  const traducirMotivo = (error, motivo, i) =>
    Utils.translateBackendKey(motivo, (error.motivosContext || [])[i] || null, intl);

  const handleDescargarErrores = () => {
    if (!resultado || !resultado.errores || resultado.errores.length === 0) return;
    setDescargando(true);
    // Se traduce ACÁ, antes de mandar: el Excel lo arma el backend, que no tiene el catálogo de
    // i18n. Si se mandaran las claves crudas, la columna "Motivo del error" le mostraría al
    // usuario cosas como "panol.items.errors.valor_supera_maximo".
    const errores = resultado.errores.map((error) => ({
      ...error,
      motivos: (error.motivos || []).map((motivo, i) => traducirMotivo(error, motivo, i)),
    }));
    Utils.dataBlob(`${BASE_URL}/exportar-errores-importacion`, 'POST', { errores })
      .then((res) => descargarBlob(res.filename, res.data))
      // Ídem: el error ya lo muestra Utils.dataBlob.
      .catch(() => {})
      .finally(() => setDescargando(false));
  };

  const handleArchivoChange = (event) => {
    const seleccionado = event.target.files && event.target.files.length > 0 ? event.target.files[0] : null;
    setArchivo(seleccionado);
    setResultado(null);
  };

  const handleImportar = () => {
    if (!archivo) return;

    const formData = new FormData();
    formData.append('file', archivo);

    setImportando(true);
    axios({
      method: 'POST',
      url: Config.get('apiUrlBase') + BASE_URL + '/importar',
      data: formData,
      headers: {
        // Sin Content-Type explícito: el browser tiene que poner el boundary del multipart.
        'Authorization-Token': localStorage.getItem('token'),
      },
    })
      .then((res) => {
        setResultado(res.data);
        if (res.data && res.data.importados > 0 && props.onImportado) {
          props.onImportado();
        }
      })
      .catch((err) => {
        const data = err && err.response ? err.response.data : null;
        // Forma {mensaje, context} de BadRequestException, que es la que el front sabe interpolar.
        // Leer `data.error` en cambio daría un ARRAY de objetos y Swal mostraría "[object Object]".
        const clave = data && data.mensaje ? data.mensaje : null;
        Swal.fire({
          icon: 'error',
          title: intl.formatMessage({
            id: 'inventarioManoObra.import.error_al_importar',
            defaultMessage: 'No se pudo procesar el archivo',
          }),
          text: clave
            ? Utils.translateBackendKey(clave, data.context || null, intl)
            : intl.formatMessage({
                id: 'inventarioManoObra.import.revise_el_formato',
                defaultMessage: 'Revise que el archivo tenga el formato de la plantilla.',
              }),
          confirmButtonText: 'OK',
        });
      })
      .finally(() => setImportando(false));
  };

  const handleCerrar = () => {
    setArchivo(null);
    setResultado(null);
    props.onClose();
  };

  const hayErrores = resultado && resultado.errores && resultado.errores.length > 0;

  return (
    <Dialog open={props.open} onClose={handleCerrar} maxWidth="md" fullWidth>
      <DialogTitle>
        <FormattedMessage
          id="inventarioManoObra.import.titulo"
          defaultMessage="Importación masiva de mano de obra"
        />
      </DialogTitle>

      <DialogContent dividers>
        <div className="mb-3">
          <button className="btn btn-primary" disabled={descargando} onClick={handleDescargarPlantilla}>
            <i className="fa fa-download mr-1" />
            <FormattedMessage id="inventarioManoObra.import.descargar_plantilla" defaultMessage="Descargar plantilla" />
          </button>
          <div className="text-muted mt-1" style={{ fontSize: 12 }}>
            <FormattedMessage
              id="inventarioManoObra.import.ayuda_plantilla"
              defaultMessage="La fila 2 de la plantilla es un ejemplo y no se importa. Cargue los datos desde la fila 3. Categoría y Subcategoría se completan con el nombre, no con el ID, y la Subcategoría debe pertenecer a la Categoría indicada."
            />
          </div>
        </div>

        <div className="form-group">
          <label className="label-control" htmlFor="archivoImportacionManoObra">
            <FormattedMessage id="inventarioManoObra.import.archivo" defaultMessage="Archivo (.xlsx o .xls)" />
          </label>
          <input
            id="archivoImportacionManoObra"
            type="file"
            className="form-control"
            accept=".xlsx,.xls"
            onChange={handleArchivoChange}
          />
        </div>

        {importando && (
          <div className="text-center my-3">
            <CircularProgress />
          </div>
        )}

        {resultado && (
          <div className="mt-3">
            <div className="d-flex flex-wrap" style={{ gap: 24 }}>
              <div>
                <strong>
                  <FormattedMessage id="inventarioManoObra.import.procesadas" defaultMessage="Filas procesadas" />:
                </strong>{' '}
                {resultado.procesadas}
              </div>
              <div className="text-success">
                <strong>
                  <FormattedMessage id="inventarioManoObra.import.importados" defaultMessage="Importados" />:
                </strong>{' '}
                {resultado.importados}
              </div>
              <div className={resultado.conError > 0 ? 'text-danger' : ''}>
                <strong>
                  <FormattedMessage id="inventarioManoObra.import.con_error" defaultMessage="Con error" />:
                </strong>{' '}
                {resultado.conError}
              </div>
            </div>

            {hayErrores && (
              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>
                    <FormattedMessage
                      id="inventarioManoObra.import.detalle_errores"
                      defaultMessage="Detalle de errores"
                    />
                  </strong>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    disabled={descargando}
                    onClick={handleDescargarErrores}
                  >
                    <i className="fa fa-download mr-1" />
                    <FormattedMessage
                      id="inventarioManoObra.import.descargar_errores"
                      defaultMessage="Descargar filas con error"
                    />
                  </button>
                </div>
                <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                  <table className="table table-sm table-bordered mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: 70 }}>
                          <FormattedMessage id="inventarioManoObra.import.columna_fila" defaultMessage="Fila" />
                        </th>
                        <th style={{ width: 160 }}>
                          <FormattedMessage
                            id="inventarioManoObra.import.columna_numero_de_parte"
                            defaultMessage="Nº de parte"
                          />
                        </th>
                        <th>
                          <FormattedMessage id="inventarioManoObra.import.columna_motivos" defaultMessage="Motivos" />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultado.errores.map((error, indice) => (
                        <tr key={'error_' + indice}>
                          <td>{error.fila}</td>
                          <td>{error.numeroDeParte || '-'}</td>
                          <td>
                            <ul className="mb-0 pl-3">
                              {(error.motivos || []).map((motivo, i) => (
                                <li key={'motivo_' + indice + '_' + i}>{traducirMotivo(error, motivo, i)}</li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <button className="btn btn-primary mr-1" disabled={!archivo || importando} onClick={handleImportar}>
          <i className={importando ? 'la la-spinner spinner mr-1' : 'fa fa-check-circle mr-1'} />
          <FormattedMessage id="inventarioManoObra.import.importar" defaultMessage="Importar" />
        </button>
        <button className="btn btn-danger" onClick={handleCerrar}>
          <i className="fa fa-times-circle mr-1" />
          <FormattedMessage id="inventarioManoObra.import.cerrar" defaultMessage="Cerrar" />
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default InventarioManoObraImportModal;
